import { useCallback, useMemo, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import {
  closestCenter,
  getFirstCollision,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {
  CollisionDetection,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
  SelectiveMouseSensor,
  SelectivePointerSensor,
} from 'src/components/workflow/board/utils/keyboardCoordinateGetter';
import { useMovePlaybookRule, useMovePlaybookRuleCategory } from 'src/hooks';

import {
  findRuleCategoryId,
  findRuleDrop,
  getCanEnterCategory,
  getLandedRank,
  getRuleOrder,
  moveRuleToCategory,
  moveRuleWithinCategory,
} from './rule-drag-order';
import type { RuleOrder } from './rule-drag-order';
import type { PlaybookDetail, PlaybookDetailRule } from './types';

/** How far a pointer travels before a press becomes a drag. */
const DRAG_ACTIVATION_DISTANCE_PX = 8;
const NO_CLOSED_CATEGORIES: ReadonlySet<UUID> = new Set();

/**
 * Where a drop left the dragged entity, with the cached categories it was
 * made against: a category at its new index, or a rule in the order the drag
 * ended with, each at the rank it is expected to get.
 */
type Landed =
  | {
      base: PlaybookDetail['categories'];
      categoryId: UUID;
      kind: 'category';
      rank: string;
      toIndex: number;
    }
  | {
      base: PlaybookDetail['categories'];
      kind: 'rule';
      order: RuleOrder;
      rank: string;
      ruleId: UUID;
    };

/**
 * Dragging on the playbook detail page: rules between and within categories,
 * and categories within the playbook. One `DndContext` serves both, and which
 * kind of drag is happening is read off the active id — category ids are the
 * keys of the rule order, rule ids are in its lists.
 *
 * A category drag is a plain sortable list: the card itself follows the
 * pointer, its siblings shift out of the way, and the drop sends the
 * category's new index. Nothing is remounted, so there is no draft to keep.
 *
 * A rule drag is not: while it is in progress the list renders from a local
 * copy of the order (`draftOrder`), which `onDragOver` rewrites as the rule
 * crosses into another category — that is what makes the other category's
 * rules part to make room. The drop then sends one mutation naming the rule,
 * the category it landed in and its index there, and the draft is discarded.
 *
 * Either kind of drop is shown twice over. The mutation writes the new order
 * into the cache optimistically, but that write lands a tick after the drop,
 * behind an awaited step, and a tick is too late: dnd-kit starts easing the
 * card's drag offset back to zero the moment the drag ends, so an order that
 * changes afterwards draws the card at its new slot plus the leftover offset
 * and it slides in from the wrong side. So the drop also sets `landed`, which
 * applies the same order and rank in the drop's own render and steps aside
 * once the cache changes.
 *
 * Either way the rank is generated twice on purpose. The server's, generated
 * under a lock from the neighbours it reads, is the one that is kept. The
 * client's, generated here from the neighbours it rendered, only fills the
 * optimistic row until the reply arrives — without it the cache would briefly
 * hold a row whose rank contradicts its place in the list.
 */
export function usePlaybookDragAndDrop(
  playbook: PlaybookDetail,
  options: {
    /**
     * Categories no rule can be dragged into: the ones that are themselves a
     * pending suggestion, since rejecting one deletes every rule in it.
     */
    closedCategoryIds?: ReadonlySet<UUID>;
    /**
     * Runs before a drop is sent, with the dragged id. Manual drags win over
     * suggested moves: the review layer discards a pending move on the entity
     * here, since the user's own placement is the decision.
     */
    onBeforeDrop?: (entityId: UUID) => Promise<void>;
    /** Runs when a rule is dropped on a closed category, so the page can say why nothing moved. */
    onClosedCategoryDrop?: () => void;
  } = {}
) {
  const { closedCategoryIds = NO_CLOSED_CATEGORIES, onBeforeDrop, onClosedCategoryDrop } = options;
  const { movePlaybookRule } = useMovePlaybookRule();
  const { movePlaybookRuleCategory } = useMovePlaybookRuleCategory();
  const [activeId, setActiveId] = useState<UUID | null>(null);
  /**
   * What the current or last drag picked up. Kept after the drag ends,
   * because the drag overlay reads its drop animation in the very render the
   * drag ends in, when `activeId` is already null.
   */
  const [dragKind, setDragKind] = useState<'category' | 'rule' | null>(null);
  const [draftOrder, setDraftOrder] = useState<RuleOrder | null>(null);
  const [landed, setLanded] = useState<Landed | null>(null);

  const { categories: serverCategories } = playbook;
  /**
   * The drop only counts against the cached categories it was made from.
   * Once those change (the optimistic write, the server's reply, a rollback)
   * it is ignored, so it cannot outlive the data it reordered and needs no
   * effect to clear it.
   */
  const liveLanded = landed?.base === serverCategories ? landed : null;
  const serverOrder = useMemo(() => getRuleOrder(serverCategories), [serverCategories]);
  const order = draftOrder ?? (liveLanded?.kind === 'rule' ? liveLanded.order : serverOrder);

  /**
   * The categories as the list shows them: a category just dropped sits at
   * its new index with its expected rank, since the cards are laid out by
   * rank and the sortable list by this array's order, and both have to agree
   * in the render the drop happens in.
   */
  const categories = useMemo(() => {
    if (liveLanded?.kind !== 'category') return serverCategories;
    const { categoryId, rank, toIndex } = liveLanded;
    const fromIndex = serverCategories.findIndex(category => category.id === categoryId);
    if (fromIndex === -1) return serverCategories;
    return arrayMove(
      serverCategories.map(category =>
        category.id === categoryId ? { ...category, rank } : category
      ),
      fromIndex,
      toIndex
    );
  }, [liveLanded, serverCategories]);

  const rulesById = useMemo(
    () =>
      new Map<UUID, PlaybookDetailRule>(
        serverCategories.flatMap(category => category.rules.map(rule => [rule.id, rule]))
      ),
    [serverCategories]
  );
  const activeRule = activeId ? (rulesById.get(activeId) ?? null) : null;

  /**
   * What each category shows: the rules in `order`, resolved to their data.
   * A rule that is in the order but no longer in the playbook is left out. A
   * rule just dropped carries its expected rank, because each card lays its
   * rules out by rank.
   */
  const rulesByCategoryId = useMemo((): Record<UUID, PlaybookDetailRule[]> => {
    const landedRule = liveLanded?.kind === 'rule' ? liveLanded : null;
    return Object.fromEntries(
      Object.entries(order).map(([categoryId, ruleIds]) => [
        categoryId,
        ruleIds.flatMap(ruleId => {
          const rule = rulesById.get(ruleId);
          if (!rule) return [];
          return [landedRule?.ruleId === ruleId ? { ...rule, rank: landedRule.rank } : rule];
        }),
      ])
    );
  }, [liveLanded, order, rulesById]);

  /**
   * Collision handling sees the order as it was when the last event fired, so
   * a ref lets it read the latest draft. The last hit is remembered for the
   * frames right after a rule changes category, when the layout shifts under
   * the pointer and nothing registers a hit; falling back to it stops the rule
   * flickering back to where it came from.
   */
  const orderRef = useRef(order);
  orderRef.current = order;
  const lastOverIdRef = useRef<string | null>(null);

  const sensors = useSensors(
    useSensor(SelectivePointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    }),
    useSensor(SelectiveMouseSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    })
  );

  /**
   * A category drag only ever lands among the other category cards, so those
   * are the only targets it sees.
   *
   * A rule drag resolves in two steps: the category comes from whatever the
   * pointer is inside (a rule or a category card), then the target within it
   * is the rule whose centre the dragged row is nearest. The first step is
   * what moves a rule between categories; the second gives the usual sortable
   * feel within one, where a rule gives up its slot once the dragged row
   * passes its middle rather than the moment the pointer touches its edge.
   * Only an empty category is a target in its own right, which is what lets a
   * rule be dropped into it.
   */
  const collisionDetection: CollisionDetection = useCallback(args => {
    const currentOrder = orderRef.current;

    if (String(args.active.id) in currentOrder) {
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter(
          container => String(container.id) in currentOrder
        ),
      });
    }

    const pointerCollisions = pointerWithin(args);
    const collisions = pointerCollisions.length ? pointerCollisions : rectIntersection(args);
    const overId = getFirstCollision(collisions, 'id');
    if (overId === null) {
      return lastOverIdRef.current ? [{ id: lastOverIdRef.current }] : [];
    }

    const categoryId = findRuleCategoryId(currentOrder, String(overId));
    if (!categoryId) {
      lastOverIdRef.current = String(overId);

      return collisions;
    }

    const ruleIds = currentOrder[categoryId];
    const ruleCollisions = ruleIds.length
      ? closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter(container =>
            ruleIds.includes(String(container.id))
          ),
        })
      : [];
    const resolved = ruleCollisions.length ? ruleCollisions : [{ id: categoryId }];
    lastOverIdRef.current = String(resolved[0].id);

    return resolved;
  }, []);

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent): void => {
      const id = String(active.id);
      const isCategory = id in serverOrder;
      setActiveId(id);
      setDragKind(isCategory ? 'category' : 'rule');
      // Only a rule drag needs the draft; a category card is never remounted.
      setDraftOrder(isCategory ? null : serverOrder);
      lastOverIdRef.current = null;
    },
    [serverOrder]
  );

  const handleDragOver = useCallback(
    ({ active, over }: DragOverEvent): void => {
      if (!over) return;

      const ruleId = String(active.id);
      const overId = String(over.id);
      setDraftOrder(current => {
        if (!current || ruleId in current) return current;

        const toCategoryId = findRuleCategoryId(current, overId);
        const fromCategoryId = findRuleCategoryId(current, ruleId);
        if (!toCategoryId || !fromCategoryId || toCategoryId === fromCategoryId) return current;
        // The closed category does not part to make room, and the drop sends nothing.
        const homeCategoryId = findRuleCategoryId(serverOrder, ruleId);
        if (!getCanEnterCategory({ closedCategoryIds, homeCategoryId, toCategoryId }))
          return current;

        /**
         * Whether the rule is being carried in from below the rule it is over,
         * in which case it should slot in after that rule rather than before it.
         */
        const draggedRect = active.rect.current.translated;
        const isBelowOver = Boolean(
          draggedRect && draggedRect.top > over.rect.top + over.rect.height
        );

        return moveRuleToCategory(current, { isBelowOver, overId, ruleId, toCategoryId });
      });
    },
    [closedCategoryIds, serverOrder]
  );

  const handleDragCancel = useCallback((): void => {
    setActiveId(null);
    setDraftOrder(null);
  }, []);

  /**
   * Shows a drop in the render it happens in, then sends it. The override is
   * cleared afterwards only for the case where nothing changed the cache (a
   * refused move), which would otherwise leave the dropped order on screen.
   */
  const showThenSend = useCallback((next: Landed | null, send: () => Promise<unknown>): void => {
    if (next) setLanded(next);
    void (async () => {
      try {
        await send();
      } finally {
        if (next) setLanded(current => (current === next ? null : current));
      }
    })();
  }, []);

  const dropCategory = useCallback(
    (categoryId: UUID, overId: UUID): void => {
      const categoryIds = serverCategories.map(category => category.id);
      const fromIndex = categoryIds.indexOf(categoryId);
      const toIndex = categoryIds.indexOf(overId);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;

      const category = serverCategories.at(fromIndex);
      if (!category) return;
      const siblingRanks = arrayMove(serverCategories, fromIndex, toIndex)
        .filter(sibling => sibling.id !== categoryId)
        .map(sibling => sibling.rank);
      // Null when it cannot be shown early; the server still lands it correctly.
      const rank = getLandedRank(siblingRanks, toIndex);

      showThenSend(
        rank ? { base: serverCategories, categoryId, kind: 'category', rank, toIndex } : null,
        async () => {
          await onBeforeDrop?.(categoryId);
          await movePlaybookRuleCategory({
            input: { categoryId, toIndex },
            optimisticCategory: rank ? { ...category, rank } : undefined,
            playbookId: playbook.id,
          });
        }
      );
    },
    [movePlaybookRuleCategory, onBeforeDrop, playbook.id, serverCategories, showThenSend]
  );

  const dropRule = useCallback(
    (ruleId: UUID, overId: UUID): void => {
      const rule = rulesById.get(ruleId);
      const startedAt = findRuleDrop(serverOrder, ruleId);
      if (!rule || !startedAt || !draftOrder) return;

      /**
       * Any cross-category move has already been applied to the draft by
       * `onDragOver`; what is left is the final slot within that category. Over
       * the category itself rather than a rule in it, the draft already has
       * the rule where it goes.
       */
      const overCategoryId = findRuleCategoryId(draftOrder, overId);
      const canEnter =
        !overCategoryId ||
        getCanEnterCategory({
          closedCategoryIds,
          homeCategoryId: startedAt.categoryId,
          toCategoryId: overCategoryId,
        });
      if (!canEnter) onClosedCategoryDrop?.();
      const finalOrder = overCategoryId
        ? moveRuleWithinCategory(draftOrder, overCategoryId, ruleId, overId)
        : draftOrder;
      const drop = findRuleDrop(finalOrder, ruleId);
      if (!drop) return;
      if (drop.categoryId === startedAt.categoryId && drop.toIndex === startedAt.toIndex) return;

      const siblingIds = finalOrder[drop.categoryId].filter(id => id !== ruleId);
      const siblingRanks = siblingIds.flatMap(id => rulesById.get(id)?.rank ?? []);
      /**
       * A missing sibling means the list changed under the drag; the server
       * still lands the rule correctly, it just cannot be shown early.
       */
      const rank =
        siblingRanks.length === siblingIds.length
          ? getLandedRank(siblingRanks, drop.toIndex)
          : null;

      showThenSend(
        rank ? { base: serverCategories, kind: 'rule', order: finalOrder, rank, ruleId } : null,
        async () => {
          await onBeforeDrop?.(ruleId);
          await movePlaybookRule({
            fromCategoryId: startedAt.categoryId,
            input: { categoryId: drop.categoryId, ruleId, toIndex: drop.toIndex },
            optimisticRule: rank ? { ...rule, rank } : undefined,
          });
        }
      );
    },
    [
      closedCategoryIds,
      draftOrder,
      movePlaybookRule,
      onBeforeDrop,
      onClosedCategoryDrop,
      rulesById,
      serverCategories,
      serverOrder,
      showThenSend,
    ]
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent): void => {
      /**
       * The drop sets `landed` in the same render batch as the draft being
       * cleared here, so the list never shows the old order in between. The
       * optimistic cache write cannot do that: it lands a tick later.
       */
      setActiveId(null);
      setDraftOrder(null);
      if (!over) return;

      const id = String(active.id);
      const overId = String(over.id);
      if (id in serverOrder) dropCategory(id, overId);
      else dropRule(id, overId);
    },
    [dropCategory, dropRule, serverOrder]
  );

  return {
    activeRule,
    categories,
    collisionDetection,
    dragKind,
    handleDragCancel,
    handleDragEnd,
    handleDragOver,
    handleDragStart,
    rulesByCategoryId,
    sensors,
  };
}
